import { TRAVEL_INSURANCE_STEPS } from '@app/app/esales/travel/_sections/steps/TRAVEL_INSURANCE_STEPS';
import { PaymentMethodType } from '@app/domain/model/PaymentMethodType';
import { validateCompanyCode } from '@app/lib/company-code/validateCompanyCode';
import { parseLithuanianPersonalCode } from '@app/lib/personal-code/parseLithuanianPersonalCode';
import { isValidPhoneNumber } from 'libphonenumber-js';
import { array, boolean, number, object, string } from 'yup';

export const PurchaseTravelInsuranceSteps = {
    [TRAVEL_INSURANCE_STEPS.TRAVELERS_SETUP]: object({
        discountCode: string().optional(),
        personalCode: string()
            .min(1, 'profile.validation.fieldRequired')
            .required('profile.validation.fieldRequired')
            .test(
                'is-valid-personal-code',
                'eshop.travel.validation.personalCodeInvalid',
                value => !!value && (parseLithuanianPersonalCode(value).isValid || validateCompanyCode(value).isValid),
            )
            .test('is-18-or-older', 'eshop.travel.validation.policyholderMinAgeViolation', value => {
                if (!value) return false;

                if (validateCompanyCode(value).isValid) return true;

                const parsed = parseLithuanianPersonalCode(value);

                if (!parsed.isValid || parsed.age === undefined) return false;

                return parsed.age >= 18;
            }),
        insuredPersons: array()
            .of(
                object({
                    age: number()
                        .min(1, 'eshop.travel.validation.minAgeViolation')
                        .max(74, 'eshop.travel.validation.maxAgeViolation')
                        .required('profile.validation.fieldRequired'),
                    isPolicyholder: boolean().required(),
                    name: string(),
                    surname: string(),
                    personalCode: string(),
                }),
            )
            .min(1)
            .required(),
    }),
    [TRAVEL_INSURANCE_STEPS.CHOOSE_INSURANCE]: object({
        selectedRisks: array()
            .of(
                object({
                    code: string().required(),
                }),
            )
            .min(1, 'eshop.travel.validation.selectAtLeastOneRisk'),
    }),
    [TRAVEL_INSURANCE_STEPS.CUSTOMER_LOGIN]: object({}),
    [TRAVEL_INSURANCE_STEPS.POLICYHOLDER_INFORMATION]: object({
        policyholderFirstName: string()
            .min(1, 'profile.validation.fieldRequired')
            .required('profile.validation.fieldRequired'),
        policyholderLastName: string().when('$isCompany', {
            is: false,
            then: schema =>
                schema.min(1, 'profile.validation.fieldRequired').required('profile.validation.fieldRequired'),
            otherwise: schema => schema.optional(),
        }),
        policyholderStreet: string()
            .min(1, 'profile.validation.fieldRequired')
            .required('profile.validation.fieldRequired'),
        policyholderHouseNumber: string()
            .min(1, 'profile.validation.fieldRequired')
            .required('profile.validation.fieldRequired'),
        policyholderCity: string()
            .min(1, 'profile.validation.fieldRequired')
            .required('profile.validation.fieldRequired'),
        policyholderPostalCode: string()
            .min(1, 'profile.validation.fieldRequired')
            .required('profile.validation.fieldRequired'),
        policyholderCountry: string()
            .min(1, 'profile.validation.fieldRequired')
            .required('profile.validation.fieldRequired'),
        policyholderPhoneNumber: string()
            .required('auth.validation.invalidPhoneNumber')
            .test('is-valid-phone', 'auth.validation.invalidPhoneNumber', value => {
                if (!value) {
                    return false;
                }

                return isValidPhoneNumber(value);
            }),
        policyholderEmail: string()
            .email('profile.validation.invalidEmail')
            .min(1, 'profile.validation.fieldRequired')
            .required('profile.validation.fieldRequired'),
        policyholderIsTraveler: boolean().optional(),
    }),
    [TRAVEL_INSURANCE_STEPS.INSURED_PERSONS]: object({
        insuredPersons: array()
            .of(
                object({
                    age: number().required(),
                    isPolicyholder: boolean().required(),
                    name: string()
                        .min(1, 'profile.validation.fieldRequired')
                        .required('profile.validation.fieldRequired'),
                    surname: string()
                        .min(1, 'profile.validation.fieldRequired')
                        .required('profile.validation.fieldRequired'),
                    personalCode: string()
                        .min(1, 'profile.validation.fieldRequired')
                        .required('profile.validation.fieldRequired')
                        .test(
                            'is-valid-personal-code',
                            'eshop.travel.validation.personalCodeInvalid',
                            value =>
                                !!value &&
                                (parseLithuanianPersonalCode(value).isValid || validateCompanyCode(value).isValid),
                        )
                        .test('age-matches-personal-code', 'eshop.travel.ageMismatchError', function (value) {
                            if (!value) return false;

                            const parsedData = parseLithuanianPersonalCode(value);
                            if (!parsedData.isValid || parsedData.age === undefined) {
                                return false;
                            }

                            const { age } = this.parent;
                            return parsedData.age === age;
                        }),
                }),
            )
            .min(1)
            .required()
            .test('unique-personal-codes', 'eshop.travel.duplicatePersonalCode', value => {
                if (!value) {
                    return false;
                }
                const personalCodes = value
                    .map(person => person.personalCode)
                    .filter(code => code && code.trim() !== '');
                return new Set(personalCodes).size === personalCodes.length;
            }),
    }),
    [TRAVEL_INSURANCE_STEPS.SUMMARY]: object({
        acceptToPrivacyPolicy: boolean()
            .oneOf([true], 'eshop.travel.validation.mustAcceptPrivacyPolicy')
            .required('eshop.travel.validation.mustAcceptPrivacyPolicy'),
        acceptToTravelRules: boolean()
            .oneOf([true], 'eshop.travel.validation.mustAcceptTravelRules')
            .required('eshop.travel.validation.mustAcceptTravelRules'),
        acceptToMarketingCnl: boolean().optional(),
        acceptToMarketingCl: boolean().optional(),
    }),
    [TRAVEL_INSURANCE_STEPS.PAYMENT.METHOD_TYPE]: object({
        paymentMethodType: string()
            .required('profile.validation.fieldRequired')
            .oneOf(
                [PaymentMethodType.Card, PaymentMethodType.Banks],
                'eshop.general.validation.invalidPaymentMethodType',
            ),
        acceptCardAndPaymentProviderRules: boolean().when('paymentMethodType', {
            is: PaymentMethodType.Card,
            then: schema => schema.is([true], 'eshop.general.validation.mustAcceptPaymentProviderRules'),
            otherwise: schema => schema.optional(),
        }),
    }),
    [TRAVEL_INSURANCE_STEPS.PAYMENT.BANKS]: object({
        paymentMethod: string()
            .min(1, 'eshop.general.validation.invalidPaymentMethod')
            .when('paymentMethodType', {
                is: 'banks',
                then: schema => schema.required('profile.validation.fieldRequired'),
            }),
    }),
};
